/**
 * exportShare — cross-platform "share text" and "export file" helpers.
 *
 * Web / PWA:   uses navigator.share when available, else a Blob download for
 *              files and a clipboard copy for plain text.
 * Native:      uses @capacitor/share; for files it first writes to the cache
 *              directory via @capacitor/filesystem, then shares the file URI so
 *              the OS share sheet offers "Save to Files", Mail, etc.
 *
 * General sharing degrades gracefully. Account data exports use a stricter
 * file-only native path so private JSON is never shared as raw text.
 */
import { Capacitor } from '@capacitor/core';

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Trigger a real file download in the browser. */
function browserDownload(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Share arbitrary text (a note, a draft) via the native share sheet or the
 * Web Share API. Returns true if a share/copy actually happened.
 */
export async function shareText(opts: { title?: string; text: string; dialogTitle?: string }): Promise<boolean> {
  const { title, text, dialogTitle } = opts;

  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, dialogTitle: dialogTitle ?? title });
      return true;
    } catch (err) {
      // fall through to web path
      console.warn('Native share failed, falling back', err);
    }
  }

  // Web Share API (mobile browsers, some desktops)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text });
      return true;
    } catch (err) {
      // User cancelled — treat as no-op, not an error worth surfacing
      if (err instanceof DOMException && err.name === 'AbortError') return false;
    }
  }

  // Last resort: copy to clipboard
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  return false;
}

/**
 * Export a generated file (.md / .ics / .bib / .ris). On web this downloads the
 * file; on native it writes to cache and opens the share sheet. Callers can
 * require a file-only native result to avoid falling back to raw text.
 */
export async function exportFile(opts: {
  filename: string;
  content: string;
  mimeType: string;
  dialogTitle?: string;
  nativeFileOnly?: boolean;
}): Promise<boolean> {
  const { filename, content, mimeType, dialogTitle, nativeFileOnly = false } = opts;

  if (isNative()) {
    try {
      const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);
      const written = await Filesystem.writeFile({
        path: filename,
        data: content,
        directory: Directory.Cache,
        encoding: Encoding.UTF8,
      });
      await Share.share({ title: filename, url: written.uri, dialogTitle: dialogTitle ?? filename });
      return true;
    } catch (err) {
      if (nativeFileOnly) {
        if (err instanceof Error && (err.name === 'AbortError' || err.message === 'Share canceled')) {
          return false;
        }
        throw err;
      }
      console.warn('Native file export failed, falling back to text share', err);
      // Degrade to sharing the raw text so the user still gets their data out
      return shareText({ title: filename, text: content, dialogTitle });
    }
  }

  browserDownload(filename, content, mimeType);
  return true;
}

/** Keep account JSON in app-private cache until the user chooses a share destination. */
export async function exportAccountFile(opts: {
  filename: string;
  content: string;
}): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const native = isNative();
  const exported = await exportFile({
    ...opts,
    mimeType: 'application/json',
    dialogTitle: 'Save or share my data',
    nativeFileOnly: true,
  });
  if (!exported) return 'cancelled';
  return native ? 'shared' : 'downloaded';
}

/** Slugify a string for use in a filename. */
export function slugify(s: string, fallback = 'export'): string {
  const slug = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return slug || fallback;
}
